"use client"

import { useTransition } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Filter, RefreshCw } from "lucide-react"

export default function DrinkFilters() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()

  const currentCategory = searchParams?.get("category")
  const currentAlcoholic = searchParams?.get("alcoholic")

  const categories = [
    { value: "cocktails", label: "Cocktails" },
    { value: "mocktails", label: "Mocktails" },
    { value: "classics", label: "Classics" },
    { value: "tropical", label: "Tropical" },
    { value: "hot-drinks", label: "Hot Drinks" },
    { value: "wine-drinks", label: "Wine Drinks" },
    { value: "shooters", label: "Shooters" },
    { value: "punches", label: "Punches & Batch" },
  ]

  const updateFilters = (key, value) => {
    const params = new URLSearchParams(searchParams?.toString() || "")

    if (value === null) {
      params.delete(key)
    } else {
      params.set(key, value)
    }

    startTransition(() => {
      router.push(`/drinks?${params.toString()}`)
    })
  }

  const resetFilters = () => {
    startTransition(() => {
      router.push("/drinks")
    })
  }

  const hasActiveFilters = currentCategory || currentAlcoholic

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center">
          <Filter className="mr-2 h-4 w-4" />
          Filters
        </h2>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters} disabled={isPending} className="h-8 text-xs">
            <RefreshCw className="mr-1 h-3 w-3" />
            Reset
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Type</h3>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="alcoholic-yes"
              checked={currentAlcoholic === "true"}
              onCheckedChange={(checked) => updateFilters("alcoholic", checked ? "true" : null)}
            />
            <Label htmlFor="alcoholic-yes" className="text-sm">
              Alcoholic
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="alcoholic-no"
              checked={currentAlcoholic === "false"}
              onCheckedChange={(checked) => updateFilters("alcoholic", checked ? "false" : null)}
            />
            <Label htmlFor="alcoholic-no" className="text-sm">
              Non-Alcoholic
            </Label>
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h3 className="font-medium">Categories</h3>

        <div className="space-y-2">
          {categories.map((category) => (
            <div key={category.value} className="flex items-center space-x-2">
              <Checkbox
                id={`category-${category.value}`}
                checked={currentCategory === category.value}
                onCheckedChange={(checked) => updateFilters("category", checked ? category.value : null)}
              />
              <Label htmlFor={`category-${category.value}`} className="text-sm">
                {category.label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
